import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Search } from "lucide-react";
import { api } from "../../../software/api";
import { PosTile } from "../pos/PosTile";
import { QtyKeypad } from "./QtyKeypad";
import { money } from "./shared";

const asList = (r) => (Array.isArray(r) ? r : r?.items || []);

const PartRow = ({ p, onPick }) => (
  <div className="grid grid-cols-[1fr_110px_110px_80px_60px_70px] gap-2 items-center px-2 py-1.5 text-[12px] bg-white/90 border-b border-slate-200" data-testid={`wo-part-row-${p.id}`}>
    <span className="font-bold truncate" title={p.name}>{p.name}</span>
    <span className="truncate">{p.brand || "—"}</span>
    <span className="truncate text-slate-500">{p.sku || "—"}</span>
    <span className="text-right font-bold">{money(p.price)}</span>
    <span className={`text-center font-bold ${(p.qty || 0) <= 0 ? "text-rose-600" : "text-emerald-700"}`}>{p.qty ?? 0}</span>
    <button onClick={() => onPick(p)} data-testid={`wo-part-pick-${p.id}`} className="rounded bg-[#f5c518] text-black font-black py-1 hover:brightness-95">Add</button>
  </div>
);

// Legacy Form_AddWorkOrderPart: its own picture tiles (Parts Layout) + part search, then the quantity keypad.
export default function AddWorkOrderPart({ onAdd, onClose }) {
  const [tabs, setTabs] = useState([]);
  const [tiles, setTiles] = useState([]);
  const [tabId, setTabId] = useState(null);
  const [f, setF] = useState({ q: "", brand: "", sku: "" });
  const [res, setRes] = useState([]);
  const [pick, setPick] = useState(null);
  useEffect(() => {
    Promise.all([api("GET", "/pos-tabs?layout=parts"), api("GET", "/pos-tiles?layout=parts")]).then(([tb, tl]) => { setTabs(tb); setTiles(tl); setTabId(tb[0]?.id); }).catch(() => {});
  }, []);
  useEffect(() => {
    const q = [f.q, f.brand, f.sku].filter((x) => x.trim()).join(" ");
    if (!q) { setRes([]); return; }
    // only the answer for the latest letters is shown (a slow answer for an earlier letter is ignored)
    let live = true;
    const t = setTimeout(() => api("GET", `/products?q=${encodeURIComponent(q)}&limit=60`).then((r) => live && setRes(asList(r))).catch(() => live && setRes([])), 250);
    return () => { live = false; clearTimeout(t); };
  }, [f]);
  const tapTile = async (t) => {
    if (!t.product_id) { setF({ ...f, q: t.label }); return; }
    const p = await api("GET", `/products/${t.product_id}`).catch(() => null);
    setPick(p ? { id: p.id, name: p.name, price: p.price || 0 } : { id: null, name: t.label, price: t.price || 0 });
  };
  const inp = "rounded border border-slate-300 px-2 py-1.5 text-[12px] bg-white";
  return createPortal(
    <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-3" onClick={onClose} data-testid="wo-add-part-popup">
      <div className="w-full max-w-4xl max-h-[94vh] flex flex-col rounded-md overflow-hidden shadow-2xl border border-[#5b8ac4] bg-[#eef3fa]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between bg-[#123a63] text-white px-4 py-2">
          <span className="font-display font-black">Add Part to Work Order</span>
          <button onClick={onClose} data-testid="wo-add-part-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex gap-1 px-3 pt-2 border-b-2 border-[#123a63]" data-testid="wo-add-part-tabs">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTabId(t.id)} data-testid={`wo-add-part-tab-${t.name.toLowerCase()}`}
              className={`rounded-t-md px-4 py-1.5 text-[13px] font-black transition-colors ${t.id === tabId ? "bg-[#123a63] text-white" : "bg-white text-[#123a63] hover:bg-sky-50"}`}>{t.name}</button>
          ))}
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 p-3 max-h-[38vh] overflow-auto" data-testid="wo-add-part-tiles">
          {tiles.filter((t) => t.tab_id === tabId).map((t) => <PosTile key={t.id} t={t} onClick={() => tapTile(t)} testid={`wo-add-part-tile-${t.id}`} />)}
        </div>
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-[#5b7896]">
          <Search className="h-4 w-4 text-white" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Part Description" data-testid="wo-add-part-search" className={`${inp} flex-1 min-w-[180px]`} />
          <input value={f.brand} onChange={(e) => setF({ ...f, brand: e.target.value })} placeholder="Brand (ie: Aquaflow)" data-testid="wo-add-part-brand" className={`${inp} w-40`} />
          <input value={f.sku} onChange={(e) => setF({ ...f, sku: e.target.value })} placeholder="Part ID (ie: Unique Part No)" data-testid="wo-add-part-sku" className={`${inp} w-48`} />
        </div>
        <div className="flex-1 overflow-auto bg-[#7b93ad]" data-testid="wo-add-part-results">
          <div className="grid grid-cols-[1fr_110px_110px_80px_60px_70px] gap-2 px-2 py-1 text-[11px] font-black text-white sticky top-0 bg-[#3d5a78]">
            <span>Description</span><span>Brand</span><span>Part ID</span><span className="text-right">Price</span><span className="text-center">In Stock</span><span />
          </div>
          {res.map((p) => <PartRow key={p.id} p={p} onPick={(x) => setPick({ id: x.id, name: x.name, price: x.price || 0 })} />)}
          {!res.length && <p className="text-center text-white/80 text-[12px] py-4">Tap a picture above, or type a description / brand / part ID to search the parts list.</p>}
        </div>
      </div>
      {pick && <QtyKeypad item={pick} onClose={() => setPick(null)} onAdd={(qty) => { onAdd(pick, qty); setPick(null); }} />}
    </div>,
    document.body
  );
}
